// beat.ts — a backing drum groove that loops under the plink notes.
//
// Reuses the kick/snare/hat instruments from the audio palette and drives them
// with a Tone.Sequence locked to the transport, so the groove stays in time with
// everyone's plinks and the relay clock. Several curated 16-step grooves; toggle
// on/off and switch groove live.

import * as Tone from "tone";
import { getPalette } from "./audio";

export interface Groove {
  id: string;
  label: string;
  kick: number[];
  snare: number[];
  hat: number[];
}

export const GROOVES: Groove[] = [
  { id: "four", label: "four", kick: [0, 4, 8, 12], snare: [4, 12], hat: [2, 6, 10, 14] },
  { id: "boombap", label: "boom-bap", kick: [0, 7, 10], snare: [4, 12], hat: [0, 2, 4, 6, 8, 10, 12, 14] },
  { id: "house", label: "house", kick: [0, 4, 8, 12], snare: [4, 12], hat: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] },
  { id: "half", label: "half-time", kick: [0, 10], snare: [8], hat: [0, 2, 4, 6, 8, 10, 12, 14] },
];

export interface BeatEngine {
  toggle(): boolean;
  isPlaying(): boolean;
  setGroove(id: string): void;
  currentGroove(): string;
  dispose(): void;
}

export function createBeat(): BeatEngine {
  const palette = getPalette();
  const kick = palette.get("kick");
  const snare = palette.get("snare");
  const hat = palette.get("hat");

  let groove: Groove = GROOVES[0]!;
  let playing = false;

  const seq = new Tone.Sequence(
    (time, step) => {
      if (kick && groove.kick.includes(step)) kick.trigger(time, 0.85);
      if (snare && groove.snare.includes(step)) snare.trigger(time, 0.55);
      if (hat && groove.hat.includes(step)) hat.trigger(time, 0.3);
    },
    Array.from({ length: 16 }, (_, i) => i),
    "16n",
  );

  return {
    toggle(): boolean {
      playing = !playing;
      if (playing) seq.start(0);
      else seq.stop();
      return playing;
    },
    isPlaying(): boolean {
      return playing;
    },
    setGroove(id: string): void {
      const next = GROOVES.find((gr) => gr.id === id);
      if (next) groove = next;
    },
    currentGroove(): string {
      return groove.id;
    },
    dispose(): void {
      seq.stop();
      seq.dispose();
    },
  };
}
